import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TenantDirectoryTable = ({ tenants, onSuspend, onReactivate, onViewDetails }) => {
  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-success/10 text-success border-success',
      suspended: 'bg-destructive/10 text-destructive border-destructive',
      trial: 'bg-primary/10 text-primary border-primary',
      cancelled: 'bg-muted text-muted-foreground border-border'
    };
    return badges?.[status] || badges?.active;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'CheckCircle',
      suspended: 'XCircle',
      trial: 'Clock',
      cancelled: 'Ban'
    };
    return icons?.[status] || 'Circle';
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!tenants || tenants?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Building2" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">No gym locations found</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Gym Location</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Owner</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Outstanding</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Last Payment</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Failed Attempts</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tenants?.map((tenant) => (
              <tr key={tenant?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    {tenant?.logo_url ? (
                      <img src={tenant?.logo_url} alt={tenant?.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Building2" size={20} color="var(--color-primary)" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{tenant?.name}</p>
                      <p className="text-sm text-muted-foreground">{tenant?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(tenant?.status)}`}>
                    <Icon name={getStatusIcon(tenant?.status)} size={12} />
                    {tenant?.status?.charAt(0)?.toUpperCase() + tenant?.status?.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{tenant?.user_profiles?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{tenant?.user_profiles?.email}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`font-semibold ${tenant?.billing_status?.is_overdue ? 'text-destructive' : 'text-foreground'}`}>
                    {formatCurrency(tenant?.billing_status?.outstanding_balance)}
                  </span>
                  {tenant?.billing_status?.is_overdue && (
                    <div className="flex items-center gap-1 text-xs text-destructive mt-1">
                      <Icon name="AlertCircle" size={12} />
                      Overdue
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {formatDate(tenant?.billing_status?.last_payment_date)}
                </td>
                <td className="px-4 py-4">
                  <span className={`font-semibold ${tenant?.billing_status?.failed_payment_count >= 3 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {tenant?.billing_status?.failed_payment_count || 0}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewDetails(tenant)}
                    >
                      <Icon name="Eye" size={14} />
                      View
                    </Button>
                    {tenant?.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onSuspend(tenant)}
                      >
                        <Icon name="Ban" size={14} />
                        Suspend
                      </Button>
                    ) : tenant?.status === 'suspended' ? (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onReactivate(tenant?.id)}
                      >
                        <Icon name="CheckCircle" size={14} />
                        Reactivate
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {tenants?.map((tenant) => (
          <div key={tenant?.id} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              {tenant?.logo_url ? (
                <img src={tenant?.logo_url} alt={tenant?.name} className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Building2" size={24} color="var(--color-primary)" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{tenant?.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{tenant?.email}</p>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(tenant?.status)}`}>
                  <Icon name={getStatusIcon(tenant?.status)} size={12} />
                  {tenant?.status?.charAt(0)?.toUpperCase() + tenant?.status?.slice(1)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <p className="text-muted-foreground mb-1">Outstanding</p>
                <p className={`font-semibold ${tenant?.billing_status?.is_overdue ? 'text-destructive' : 'text-foreground'}`}>
                  {formatCurrency(tenant?.billing_status?.outstanding_balance)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Failed Attempts</p>
                <p className={`font-semibold ${tenant?.billing_status?.failed_payment_count >= 3 ? 'text-destructive' : 'text-foreground'}`}>
                  {tenant?.billing_status?.failed_payment_count || 0}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                fullWidth
                onClick={() => onViewDetails(tenant)}
              >
                <Icon name="Eye" size={14} />
                View Details
              </Button>
              {tenant?.status === 'active' ? (
                <Button
                  size="sm"
                  variant="destructive"
                  fullWidth
                  onClick={() => onSuspend(tenant)}
                >
                  <Icon name="Ban" size={14} />
                  Suspend
                </Button>
              ) : tenant?.status === 'suspended' ? (
                <Button
                  size="sm"
                  variant="default"
                  fullWidth
                  onClick={() => onReactivate(tenant?.id)}
                >
                  <Icon name="CheckCircle" size={14} />
                  Reactivate
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TenantDirectoryTable;